import get from 'lodash/get'
import { capitalize, normalizeIoc } from '../utils'

export function maybeRedacted(value) {
  return value === 'redacted' ? value : null
}

/** Format items to be a sorted list of the field 'name' */
export function basicFormatting(items) {
  return maybeRedacted(items) || (items || []).map((item) => item.name).sort()
}

/** Format items to be a sorted list with fields for am-charts */
export function basicChartFormatting(items) {
  return (items || []).map((item) => {
    return handleChartObj(item)
  }).sort(sortOnValue)
}

/** Fixes bad cve labeling by attempting to extract CVE from the label, dropping unmatched labels */
export function cveFormatting(items) {
  const cveRe = /(cve-\d+-\d+)/i

  return maybeRedacted(items) || (items || []).reduce((items, item) => {
    const cve = item.label && item.label.match(cveRe)?.[0]

    if (cve) {
      items.push({ ...item, label: cve })
    }

    return items
  }, [])
}

export function excludeUnknownCountry(items) {
  return maybeRedacted(items) || (items || []).filter(item => !!item.name && item.name !== 'Unknown Country')
}

/** Format items to extract name and country code and sort on name */
export function listCountryCodeFormatting(items) {
  return excludeUnknownCountry(items)
    .reduce((acc, target) => target.name ? [...acc, { name: target.name, icon: target.iso_country_code_2 }] : acc, [])
    .sort((a, b) => a.name.localeCompare(b.name))
}

export function getCountryCode(locations) {
  return maybeRedacted(locations) || get(locations, '[0].iso_country_code_2', undefined)
}

/** Format aliases into comma delimited string */
export function aliasesFormatting(aliases) {
  return maybeRedacted(aliases) || Array.from(aliases || []).sort().join(', ')
}

/** Format motivation to be a sorted list of the field 'name' */
export function motivationsFormatting(motivations) {
  return maybeRedacted(motivations) || (motivations || []).map((motivation) => capitalize(motivation))
}

/** Format Top 10 Malware for Chart */
export function topMalwareFormatting(malware) {
  return basicChartFormatting((malware || []).slice(0, 10))
}

/** Map data we need from relevant reports to an array and then sort by date with overview/group profile floated to top */
export function relevantReportsFormatting(reports, requestType) {
  if (!reports) {
    return undefined
  }
  return maybeRedacted(reports) || reports
    .reduce((acc, item) => {
      // eslint-disable-next-line camelcase
      acc.push({
        name: item.title,
        date: item.published_date || item.created,
        url: item.report_id && `/reports/${item.report_id}`,
        reportType: item.report_type || item.metadata?.report_type?.[0], // metadata.report_type will be removed by NAUT-2176
        license_info: item.license_info,
        audience: item.audience,
      })
      return acc
    }, [])
    .sort((a, b) => {
      // Float certain reports to the top
      if (!isOverviewOrProfile(a, requestType) && isOverviewOrProfile(b, requestType)) {
        return 1
      } else if (isOverviewOrProfile(a, requestType) && !isOverviewOrProfile(b, requestType)) {
        return -1
      }
      // Sort the rest by date
      if (a.date && b.date) {
        const aDate = new Date(a.date).toISOString()
        const bDate = new Date(b.date).toISOString()
        if (aDate < bDate) { return 1 }
        if (aDate > bDate) { return -1 }
      }
      return 0
    })
}

const isOverviewOrProfile = (item, requestType) => {
  const { reportType } = item
  return reportType && (reportType.includes(`${requestType} Overview`) || reportType.includes(`${requestType} Profile`))
}

/** Format ioc data into key value pairs, where the value of each key is a normalized Array */
export function iocFormatting(ioc) {
  if (maybeRedacted(ioc)) {
    return maybeRedacted(ioc)
  }

  if (!ioc) return null

  const iocs = {}
  Object.entries(ioc).forEach(([key, value]) => {
    iocs[key] = normalizeIoc({ [key]: value })
  })
  return iocs
}

/** Handle translating to a chart object */
const handleChartObj = (item) => {
  const cObj = {}
  cObj.id = item.id
  // NOTE This is a hack to allow `basicChartFormatting` for lists without metrics until we can
  // do away with compiled services in favor of more explicit data management/access
  cObj.label = item.name || item.label
  cObj.value = item.metrics ? item.metrics[0].value : undefined
  return cObj
}

/** Sort object array on Value field */
const sortOnValue = (a, b) => a.value === b.value ? b.label.localeCompare(a.label) : a.value - b.value

export default {
  aliasesFormatting,
  basicFormatting,
  basicChartFormatting,
  cveFormatting,
  getCountryCode,
  listCountryCodeFormatting,
  motivationsFormatting,
  relevantReportsFormatting,
  topMalwareFormatting
}
